import React, { useMemo } from "react";
import { formatDateTimeDisplay } from "../utils/common";
import "../assets/css/clockRoster.css";

/* The three states the procedure returns, side by side.
 *
 * This was tabs. Tabs were wrong for the slot: the panel spans the full width
 * of the dashboard, so there is room to show all three at once, and hiding two
 * thirds of the answer behind a click is a poor trade for space that was going
 * spare anyway. "Who is in and who is out" is one question — you should not
 * have to click twice to finish reading it.
 *
 * "Not In Yet" stays its own column rather than folding into Clocked Out: at
 * nine in the morning that merge reports the whole company as gone home. */
const COLUMNS = [
  {
    key: "In",
    label: "Clocked In",
    icon: "bi-box-arrow-in-right",
    tone: "green",
    empty: "Nobody is clocked in",
  },
  {
    /* Away for a few minutes, not gone.
     *
     * These used to sit in Clocked Out, and that column reads as "left for
     * the day" — three people on a tea break looked like they had finished.
     * A break says you are coming back; a clock-out does not. */
    key: "Break",
    label: "On Break",
    icon: "bi-cup-hot",
    tone: "orange",
    empty: "Nobody is on a break",
  },
  {
    key: "Out",
    label: "Clocked Out",
    icon: "bi-box-arrow-right",
    tone: "grey",
    empty: "Nobody has left yet",
  },
  {
    key: "Not In",
    label: "Not In Yet",
    icon: "bi-dash-circle",
    tone: "amber",
    empty: "Everybody has arrived",
  },
];

const initials = (name) =>
  String(name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

const clockTime = (v) => formatDateTimeDisplay(v, "hh:mm A", "--:--");

/* Why somebody is out. The work-status menu writes "Break" / "Lunch" /
   "Be right back" onto the punch; a plain end-of-day clock-out, a device
   punch or a hand-added one carries no reason at all.
 *
 * NULL becomes "Clocked out" rather than being left blank: a missing reason
 * is a fact ("they just left"), and an empty space reads as a value that
 * failed to load. */
const REASON_TONE = {
  break: "amber",
  lunch: "orange",
  "be right back": "blue",
};

const reasonOf = (row) => {
  const raw = String(row.LastOutReason || "").trim();
  if (!raw) return { label: "Clocked out", tone: "plain" };
  return { label: raw, tone: REASON_TONE[raw.toLowerCase()] || "plain" };
};

/* "2h 14m". Minutes alone stop being a quantity anybody feels somewhere
   around ninety. */
const since = (minutes) => {
  if (minutes === null || minutes === undefined) return "";
  const m = Math.max(0, Number(minutes));
  if (m < 1) return "just now";
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
};

/**
 * Who is at their desk right now.
 *
 * Separate from Live Team Status, which lists who has a task running. A person
 * can be clocked in with nothing started, or still hold an open task after
 * punching out — the two lists genuinely differ, and merging them would force
 * a wrong answer to one of the two questions.
 *
 * Props:
 *   rows    : [{ UserCode, UserName, Role, ClockState, FirstIn, LastOut,
 *                LastPunch, SinceMinutes, WorkStatus, TeamName, Color }]
 *   counts  : { in, out, notIn } — from the server, so the column headers and
 *             the lists under them cannot drift apart
 *   loading
 */
const ClockRosterPanel = ({ rows = [], counts = {}, loading = false }) => {
  const grouped = useMemo(() => {
    const out = { In: [], Break: [], Out: [], "Not In": [] };
    for (const r of rows) if (out[r.ClockState]) out[r.ClockState].push(r);
    return out;
  }, [rows]);

  /* Keyed off a map rather than a chain of ternaries, so adding a state is
     one line and cannot silently fall through to the wrong count. */
  const COUNT_KEY = { In: "in", Break: "onBreak", Out: "out", "Not In": "notIn" };
  const countFor = (key) => counts[COUNT_KEY[key]] ?? 0;

  return (
    <div className="content-card ckr">
      <div className="ckr__head">
        <h5 className="ckr__heading">
          <i className="bi bi-fingerprint" />
          Clock Status
        </h5>
        <span className="ckr__asof">
          {loading ? "Loading…" : `${rows.length} people`}
        </span>
      </div>

      <div className="ckr__board">
        {COLUMNS.map((col) => {
          const people = grouped[col.key] ?? [];

          return (
            <section key={col.key} className={`ckr__col ckr__col--${col.tone}`}>
              <header className="ckr__colhead">
                <i className={`bi ${col.icon}`} />
                <span className="ckr__collabel">{col.label}</span>
                <span className="ckr__count">{countFor(col.key)}</span>
              </header>

              <div className="ckr__people">
                {people.length === 0 ? (
                  <p className="ckr__empty">{loading ? "…" : col.empty}</p>
                ) : (
                  people.map((r) => (
                    <div key={r.UserCode} className="ckr__row">
                      <span
                        className="ckr__avatar"
                        style={r.Color ? { background: r.Color } : undefined}
                      >
                        {initials(r.UserName)}
                      </span>

                      <span className="ckr__who">
                        <span className="ckr__name">{r.UserName}</span>
                        <span className="ckr__meta">
                          {r.TeamName || "No team"}

                          {/* Why they went. Only on the Out column — on the In
                              column the reason is always "Active" or "In
                              Progress", which tells you nothing you cannot
                              already see from the fact that they are in. */}
                          {(r.ClockState === "Out" || r.ClockState === "Break") &&
                            (() => {
                              const reason = reasonOf(r);
                              return (
                                <span
                                  className={`ckr__reason ckr__reason--${reason.tone}`}
                                >
                                  {reason.label}
                                </span>
                              );
                            })()}

                          {/* No work-status badge on this side. It was showing
                              "Lunch" beside people the clock says are IN,
                              because the menu's value and the punch record can
                              disagree — a device punch brings somebody back
                              without touching the menu, so the label outlives
                              the break it described. The Clocked Out column
                              carries the reason instead, taken from the punch
                              itself, which cannot go stale. */}
                        </span>
                      </span>

                      {/* Both ends of the day, on every row. One time on its
                          own always left the other question open — somebody
                          clocked in at 14:02 tells you nothing about whether
                          they took a break at one; the pair does. The side
                          that matters right now is the bold one. */}
                      <span className="ckr__when">
                        <span className="ckr__times">
                          <span
                            className={`ckr__t${r.ClockState === "In" ? " is-now" : ""}`}
                            title="Last clock in"
                          >
                            <i className="bi bi-box-arrow-in-right" />
                            {clockTime(r.LastIn)}
                          </span>
                          <span
                            className={`ckr__t${r.ClockState === "Out" ? " is-now" : ""}`}
                            title="Last clock out"
                          >
                            <i className="bi bi-box-arrow-right" />
                            {clockTime(r.LastOut)}
                          </span>
                        </span>
                        {r.ClockState !== "Not In" && (
                          <span className="ckr__since">
                            {since(r.SinceMinutes)}
                          </span>
                        )}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
};

export default ClockRosterPanel;
